import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../lib/icons';

/**
 * A password input with a reveal toggle.
 *
 * Worth having anywhere, and newly necessary here: since invitations carry a
 * generated temporary password — `k7mqp-3xtrf-9wnjd` — people are now
 * transcribing an unfamiliar string into a field that shows them nothing. A
 * mistyped character is indistinguishable from a wrong password, and the account
 * locks after five attempts (REQ-006).
 *
 * Three things it does that a bare `type` swap does not:
 *
 *   - **Names its state, not its icon.** The accessible name is "Show password"
 *     or "Hide password", so a screen-reader user is told what pressing it does
 *     rather than what it looks like. `aria-pressed` carries the state itself.
 *   - **Re-hides on submit.** Signing in with the password on screen leaves it
 *     there through the page transition, and on a shared or projected screen
 *     that is exactly when somebody else is looking.
 *   - **Stays out of the tab order between the field and the button after it.**
 *     It sits after the input, so tabbing from the password still reaches
 *     "Sign in" next — the reveal is for the mouse and for deliberate
 *     keyboard use, not something to tab through on the way.
 */
export function PasswordField({
  id,
  label,
  value,
  onChange,
  autoComplete,
  disabled,
  revealed,
  onRevealedChange,
  describedBy,
  invalid,
  hint,
  error,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: string;
  disabled?: boolean;
  /** Lifted, so a form can hide it again when it submits. */
  revealed: boolean;
  onRevealedChange: (revealed: boolean) => void;
  describedBy?: string;
  invalid?: boolean;
  /**
   * Advisory text under the input (e.g. the length rule). Owned by this field
   * so the field's own bottom margin keeps the next label clear of it — a
   * sibling hint outside `.field` collides with whatever follows.
   */
  hint?: string;
  /** Server or client message that takes the place of the hint while set. */
  error?: string;
}): ReactElement {
  const { t } = useTranslation();

  /*
   * Prefer the error id over the hint: both are never shown at once, and the
   * announced description should be the reason the field is invalid.
   */
  const messageId = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  const described =
    [describedBy, messageId].filter((part): part is string => Boolean(part)).join(' ') || undefined;

  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>

      <div className="pw-wrap">
        <input
          id={id}
          type={revealed ? 'text' : 'password'}
          autoComplete={autoComplete}
          required
          value={value}
          /*
           * Whitespace is dropped as it is typed or pasted, rather than being
           * accepted and then refused.
           *
           * The server rejects it either way (2026-08-05), but in a masked field
           * a space is invisible: somebody who pastes a password with a trailing
           * space, or a tab a word processor left in it, sees a field that looks
           * correct and an error that looks wrong. Removing it at the point of
           * entry means the value on screen is the value that will be sent.
           *
           * `\s`, so tabs and the non-breaking space survive nothing — those are
           * the ones that come through a copy-and-paste and cannot be seen at
           * all.
           */
          onChange={(e) => onChange(e.target.value.replace(/\s/g, ''))}
          disabled={disabled}
          aria-invalid={invalid || Boolean(error) || undefined}
          aria-describedby={described}
        />

        <button
          type="button"
          className="pw-reveal"
          onClick={() => onRevealedChange(!revealed)}
          disabled={disabled}
          aria-pressed={revealed}
          aria-controls={id}
          /* The action, not the picture. */
          aria-label={revealed ? t('hide_password') : t('show_password')}
          title={revealed ? t('hide_password') : t('show_password')}
        >
          <Icon name={revealed ? 'eye-off' : 'eye'} />
        </button>
      </div>

      {error ? (
        <div id={`${id}-error`} className="field-error" role="alert">
          {error}
        </div>
      ) : hint ? (
        <span id={`${id}-hint`} className="hint">
          {hint}
        </span>
      ) : null}
    </div>
  );
}

/**
 * The reveal state, and a hider to call when a form submits.
 *
 * A hook rather than internal state so the form owns the "hide it again" moment
 * — the component cannot know when a submission has happened.
 */
export function usePasswordReveal(): {
  revealed: boolean;
  setRevealed: (value: boolean) => void;
  hide: () => void;
} {
  const [revealed, setRevealed] = useState(false);
  return { revealed, setRevealed, hide: () => setRevealed(false) };
}
